import { useState, useEffect } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, Image as ImageIcon, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

const BLANK = { caption: "", category: "Gallery", image: "", order: 0 };
const INPUT_CLS =
  "bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0";

export function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/gallery");
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load gallery items");
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  const startCreate = () => { setForm(BLANK); setEditing(null); setShowForm(true); };
  const startEdit = (it) => {
    setForm({
      ...BLANK,
      ...it,
      caption: it.caption || "",
      category: it.category || "Gallery",
      image: it.image || "",
      order: it.order ?? 0,
    });
    setEditing(it.id);
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(BLANK); };
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        caption: form.caption?.trim() || "",
        category: form.category?.trim() || "Gallery",
        image: form.image?.trim() || "",
        order: parseInt(form.order, 10) || 0,
      };
      if (editing) {
        await api.put(`/admin/gallery/${editing}`, payload);
        toast.success("Gallery photo updated");
      } else {
        const r = await api.post("/admin/gallery", payload);
        toast.success("Photo added — now upload an image below");
        setEditing(r.data.id);
        setForm((f) => ({ ...f, ...payload }));
        await load();
        setSaving(false);
        return;
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery photo permanently?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const handleDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    setItems(next);
    setDragIdx(null);
    try {
      await api.post("/admin/gallery/reorder", { ids: next.map((it) => it.id) });
      toast.success("Order saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reorder failed");
      await load();
    }
  };

  return (
    <div data-testid="gallery-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-white/55">
          {items.length} gallery photo{items.length === 1 ? "" : "s"} · shown on the About page gallery
        </p>
        <Button
          onClick={() => (showForm ? cancel() : startCreate())}
          className="btn-gold rounded-none px-6 py-4 text-xs"
          data-testid="admin-new-gallery"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Cancel" : "New Photo"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} className="surface p-8 mb-10 grid sm:grid-cols-2 gap-6" data-testid="gallery-form">
          <Field label="Caption" full>
            <Input value={form.caption} onChange={update("caption")} placeholder="Live at the Nicetown Festival" className={INPUT_CLS} data-testid="gallery-caption" />
          </Field>
          <Field label="Category (tab)">
            <Input value={form.category} onChange={update("category")} placeholder="Live" className={INPUT_CLS} data-testid="gallery-category" />
          </Field>
          <Field label="Display Order">
            <Input type="number" value={form.order} onChange={update("order")} className={INPUT_CLS} data-testid="gallery-order" />
          </Field>
          <Field label="Image URL (paste a link, or save then upload a file below)" full>
            <Input value={form.image} onChange={update("image")} placeholder="https://….jpg" className={INPUT_CLS} data-testid="gallery-image-url" />
          </Field>
          {editing && (
            <div className="sm:col-span-2 flex items-center gap-4">
              <GalleryImageUploader item={{ id: editing, image: form.image }} reload={load} onDone={(url) => setForm((f) => ({ ...f, image: url }))} />
              <span className="text-xs text-white/45">Upload the photo file here (JPG/PNG/WebP, max 10 MB).</span>
            </div>
          )}
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="btn-gold rounded-none px-8 py-4 text-xs" data-testid="gallery-save">
              {saving ? "Saving…" : editing ? "Update Photo" : "Add Photo"}
            </Button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <p className="text-white/45 py-6">No gallery photos yet. Add your first photo above.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
          {items.map((it, idx) => (
            <div
              key={it.id}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              onDragEnd={() => setDragIdx(null)}
              className={`surface p-3 flex flex-col gap-2 transition-opacity ${dragIdx === idx ? "opacity-40" : ""}`}
              data-testid={`gallery-row-${it.id}`}
            >
              <div className="flex items-center justify-between text-white/30">
                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] cursor-grab active:cursor-grabbing" title="Drag to reorder">
                  <GripVertical size={14} /> Drag
                </span>
                <span className="text-[9px] tabular-nums">#{idx + 1}</span>
              </div>
              <GalleryImageUploader item={it} reload={load} />
              <p className="text-sm text-white/70 leading-snug line-clamp-2">{it.caption || <span className="text-white/35 italic">No caption</span>}</p>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--gold)]">{it.category}</span>
              <div className="flex items-center gap-4 pt-1 border-t border-white/5 mt-auto">
                <button onClick={() => startEdit(it)} className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)]" data-testid={`gallery-edit-${it.id}`}>
                  Edit
                </button>
                <button onClick={() => remove(it.id)} className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-red-400" data-testid={`gallery-delete-${it.id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryImageUploader({ item, reload, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/gallery/${item.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded.");
      if (onDone) onDone(r.data.image);
      if (reload) await reload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Image upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label
      className="relative block w-full aspect-square border border-white/10 cursor-pointer overflow-hidden group/g"
      data-testid={`gallery-image-upload-${item.id}`}
    >
      {item.image ? (
        <img src={imgUrl(item.image)} alt="Gallery" className="w-full h-full object-cover" />
      ) : (
        <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-[9px] uppercase tracking-[0.15em] text-white/40">
          <ImageIcon size={18} /> Upload
        </span>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
      />
      <span className="absolute inset-0 bg-black/55 opacity-0 group-hover/g:opacity-100 flex items-center justify-center text-[9px] uppercase tracking-[0.15em] text-[color:var(--gold)] transition-opacity">
        {busy ? "…" : item.image ? "Replace" : "Upload"}
      </span>
    </label>
  );
}
